import { queryCache, QueryConfig, useQuery } from 'react-query';
import { Api } from '../../apiClient';
import { getQueryConfig } from '../../utils/queryUtils';

type KeyParams = Api.FetchAnalyticsSyncParams;

const KEY_START = 'analyticsSync' as const;
export const createKey = ({ organizationId, inputDatasetId }: KeyParams) =>
  [KEY_START, organizationId, inputDatasetId] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchAnalyticsSync
>;

const fetcher: Fetcher = (_, organizationId, inputDatasetId) =>
  Api.fetchAnalyticsSync({ organizationId, inputDatasetId });

type useAnalyticsSyncParams = Partial<Api.FetchAnalyticsSyncParams> & {
  config?: QueryConfig<Api.FetchAnalyticsSyncResponse, Api.ErrorResponse>;
};

export function useAnalyticsSync({
  config: inputConfig,
  organizationId,
  inputDatasetId,
}: useAnalyticsSyncParams) {
  const isEnabled = !!organizationId && !!inputDatasetId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled
    ? createKey({ organizationId, inputDatasetId })
    : undefined;

  const { data, ...queryInfo } = useQuery(key, fetcher, config);
  return { ...queryInfo, analyticsSync: data };
}

type QueryCache = ReturnType<typeof useAnalyticsSync>['analyticsSync'];
type NonEmptyQueryCache = Exclude<QueryCache, undefined>;

export function setCache(keyParams: KeyParams, item: NonEmptyQueryCache): void {
  queryCache.setQueryData(createKey(keyParams), item);
}

export function clearCache(keyParams: KeyParams): void {
  void queryCache.setQueryData(createKey(keyParams), undefined);
}
